import { logger } from '@adornis/base/logging.js';
import { loadRawEntityDataByIDOrFail } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { differenceInDays, format } from 'date-fns';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildNewsletterOptinLink, buildRegistrationsHTML } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { isDefined } from '../util.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

defineMailTemplate(MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nutzer-Teilnahme aufgehoben',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>leider mussten wir Ihre Teilnahme an der Veranstaltung revidieren. Anbei sind die Details der Veranstaltung aufgef&uuml;hrt:</p>
<p>{{changed-registrations}}</p>
<p>Sollte es sich hier um einen Fehler handeln, wenn Sie sich bei uns per Mail.</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>You have been deregisterd from the following event:</p>
<p>{{changed-registrations}}</p>
<p>The cancellation refers only to the listed event. If we have made a mistake, please contact us by e-mail.</p>
<p>You can finde a list of your event registrations for the current semester under the following link: {{login-link}}</p>
<p>We look forward to welcoming you to one of our events in the future and recommend our lecture and workshop program at anmeldung.uni-frankfurt.de for this purpose</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN),
  attachments: [],
  displayName: 'Teilnahme aufgehoben',
  editorHint: () => institutionMailHint(MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN),
});

export async function notifyNuzterTeilnahmeAufgehoben(participationID: string) {
  const participation = await loadRawEntityDataByIDOrFail(ParticipationWorkshop, participationID, {
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    state: 1,
    waitingListPlace: 1,
  });

  const product = await loadRawEntityDataByIDOrFail(Workshop, participation.productID, {
    _id: 1,
    name: 1,
    institutionID: 1,
    topic: 1,
    language: 1,
    appointments: { _id: 1, start: 1, end: 1, location: 1, name: 1, meetingLink: { link: 1 } },
  });

  const user = await loadRawEntityDataByIDOrFail(User, participation.userID, {
    _id: 1,
    _class: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
  });

  const institutionID = product.institutionID;
  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN,
  )(InstitutionMailTemplate.allFields);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const start = product.appointments?.[0]?.start;
  let daysTillEvent = start ? differenceInDays(start, new Date()) : null;
  let dates = product.appointments
    ?.map(a => a.start)
    .filter(isDefined)
    ?.map(start => format(start, 'dd.MM.yyyy'))
    .join(', ');
  if (!dates && start) dates = format(start, 'dd.LL.yyyy');

  const sendEmail = async () => {
    const registrationData = {
      ...product,
      ...participation,
      englishVersion: false,
    };

    const mailVars = {
      [MailTemplateVars.EVENT_TITLE]: product.name ?? '',
      [MailTemplateVars.PRODUCT_NAME]: product.name ?? '',
      [MailTemplateVars.EVENT_DATES]: dates,
      [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user.firstName,
      [MailTemplateVars.LAST_NAME]: user.lastName,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK]: buildNewsletterOptinLink(token, 'de'),
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN]: buildNewsletterOptinLink(token, 'en'),
      [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML([registrationData], true),
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML(
        [{ ...registrationData, englishVersion: true }],
        true,
      ),
    };

    const { subject, content } = (await assembleInstitutionMail({
      template: mailTemplate,
      infos: mailVars as any,
    })) as { subject: string; content: any };

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');
    const fullEmailRec = {
      email: user.email,
      institutionID,
      content,
      subject,
    };

    await sendInstitutionEmail(fullEmailRec);
  };

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType: MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        product?.name || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
